export default {
  id: 'swing-scanners',
  title: 'Swing Scanners',
  content: `## Swing Scanners

Swing scanners run every 30 minutes during market hours on daily OHLCV data. They detect setups suitable for multi-day trades.

There are four categories: Breakout, Pattern, Momentum, and Price Action.

---

## Breakout Scanners

### BREAKOUT_20D_HIGH
**Trigger:** Close price crosses above the highest close of the last 20 trading days.

What it means: The stock has broken out of its recent consolidation range. Buyers have overcome the supply at prior highs.

Look for: Volume above average on the breakout day. Trend in UPTREND.
Invalidated by: Close back below the breakout level the next day (false breakout) or low volume (< 1.5× average).

---

### BREAKOUT_VOLUME_SPIKE
**Trigger:** Close above 20-day high AND volume > 2.5× the 20-day average volume.

What it means: Same as BREAKOUT_20D_HIGH but with institutional-level volume confirmation. Significantly higher conviction.

> A breakout on 3–5× volume is rare and meaningful. This is one of the strongest signals in the scanner.

---

### CONSOLIDATION_BREAKOUT
**Trigger:** Price range over the last 5 days was < 3% (tight consolidation), then breaks above that range with volume > 1.5× average.

What it means: A compressed coil releasing energy. Tight consolidations before breakouts often lead to sharp, sustained moves.

---

### CLOSE_ABOVE_PREV_HIGH
**Trigger:** Today's close is above yesterday's high, and volume > 1.3× average.

What it means: Buyers pushed through and closed above the entire prior session's range. Short-term momentum signal.

---

## Pattern Scanners

### BULLISH_ENGULFING
**Trigger:** Price near a 20-day low, today's candle completely engulfs the prior candle, close above prior open.

What it means: After a downtrend or pullback, buyers overwhelmed sellers in a single session. Potential reversal at support.

Best when: At a known support level after an extended down move. Confirmed the next day with a close above the engulfing candle.

---

### HAMMER
**Trigger:** Candle with a small body at the top and a lower wick at least 2× the body length, minimal upper wick.

What it means: Sellers pushed price down significantly during the session but buyers stepped in and pushed it back. Rejection of lower prices.

Most reliable at: Clear support levels or after multi-day downtrends.

---

### MORNING_STAR
**Trigger:** 3-candle pattern — large bearish candle, small doji or indecision candle, large bullish candle closing into the first candle's body.

What it means: A 3-day transition from bearish to bullish control. One of the more reliable reversal patterns.

Look for: Third candle closes at least 50% into the first candle's body. Volume above average on day 3.

---

## Momentum Scanners

### RSI_OVERSOLD
**Trigger:** RSI(14) drops below 30.

What it means: Selling pressure has been extreme in the short term. A potential exhaustion zone — but not a standalone buy signal.

> RSI oversold is a condition, not a signal. Use it alongside a price pattern or support level.

---

### RSI_OVERBOUGHT
**Trigger:** RSI(14) rises above 70.

What it means: Strong short-term momentum. In trending markets, RSI can stay overbought for extended periods.

---

### MACD_BULLISH_CROSSOVER
**Trigger:** MACD line crosses above the signal line with a positive histogram.

What it means: Short-term momentum accelerating faster than medium-term momentum. Classic trend-following signal.

Stronger when: Crossover occurs near the zero line (not from far negative territory).

---

### MACD_BEARISH_CROSSOVER
**Trigger:** MACD line crosses below the signal line with a negative histogram.

What it means: Momentum weakening. Often precedes further downside or consolidation.

---

### GOLDEN_CROSS
**Trigger:** SMA50 is within 3% above SMA200 — approaching a structural bullish crossover.

What it means: Long-term shift from bearish to bullish structure. A lagging signal — useful for trend confirmation, not timing.

---

## Price Action Scanners

### NEAR_52W_HIGH
**Trigger:** Close price is within 3% of the 52-week high.

What it means: Approaching a major resistance zone. If it breaks through, there is no overhead resistance above — often leads to acceleration.

---

### 52W_HIGH_TODAY
**Trigger:** Close price is at or above the 52-week high.

What it means: New annual high. All sellers from the past year are in profit — removes overhead resistance entirely.

> New 52-week highs on strong volume are historically one of the most reliable momentum signals in equities.

---

### VOLUME_SPIKE
**Trigger:** Volume > 2.5× the 20-day average volume without a specific price pattern requirement.

What it means: Unusual activity — could be accumulation or distribution. Check where the close is relative to the day's range.

Close near high of day = accumulation. Close near low of day = distribution.`,
};
